/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useState, useEffect, useContext, useMemo } from "react";
import { useHistory } from "react-router-dom";
import produce from "immer";
import DatePicker from "react-datepicker";
import { PatientsContext } from "./";
import fetchHelper from "../../services/fetchHelper";
import { STATES } from "../../constants/GeneralConstants";
import { getStateAbbreviation } from "../../utils/helpers";
import { LayoutContext } from "../../components/layout";
import SearchMultiSelect from "../../components/SearchMultiSelect";
import "./information.scss";
import SyncStatusIcon from "../../components/PatientSync/SyncStatusIcon";
import useSyncHandler from "../../components/PatientSync/useSyncHandler";
import {
  findConclusionStatementsFromIds,
  retrieveAnticoagulantIds,
  getAllStatus,
  fetchUserById,
  getAnticoagulantStatementV2,
} from "../../utils/reportStatementsUtil";
import cn from "classnames";
import duplicatedProfiles from "../../assets/duplicate-profiles.png";
import Tooltip from "../../components/Tooltip/Tooltip";
import { useToasts } from "react-toast-notifications";
import { mapFilterKeys, mapKeyFiltersForPatientReport } from "../../constants/PatientListFilterConstants";
import { featureCheck } from "../../utils/feature";
import dayjs from "dayjs";
import { dateFormats } from "../../constants/DateFormatConstants";
import PreferredPhoneField from "../../components/PreferredPhoneField";
import { isValidUSPhoneNumber, PhoneType } from "../../constants/PhoneType";
import { GET_PATIENTS_API_V2_ENABLED } from "../../constants/AppConfigConstants";
import { otherPhoneWarning } from "../../constants/WarningMessages";

const Information = () => {
  const { user, appConfigStructure } = useContext(LayoutContext);
  const { patient, information, setInformation, setPatient, fetchPatient } = useContext(PatientsContext);
  const { syncStatus, syncResult, dispatchSync, isSyncSupported, hasSyncUpdate } = useSyncHandler();
  const [data, setData] = useState(patient || {});
  const history = useHistory();
  const { addToast } = useToasts();
  const [showTooltip, setShowTooltip] = useState(false);
  const [conclusionStatements, setConclusionStatements] = useState({});
  const [anticoagulantStatementsIds, setAnticoagulantStatementsIds] = useState([]);
  const [acStatus, setAcStatus] = useState([]);
  const [anticoagulantStatementSelectedByUser, setAnticoagulantStatementSelectedByUser] = useState("");
  const [acsFlag, setAcsFlag] = useState(false);
  const [ACUpdatedData, setACUpdatedData] = useState({});

  const state = patient?.patientState || "";
  const report = user?.isServiceReport || false;
  const mrnFilter = appConfigStructure[GET_PATIENTS_API_V2_ENABLED]
    ? mapKeyFiltersForPatientReport["patientLocation.mrn"]
    : mapFilterKeys["patientLocation.mrn"];
  const featureAnticoagulantStatement = featureCheck("anticoagulantStatement.enable");
  const featurePatientPreferredPhoneNumber = featureCheck("patientPreferredPhoneNumber.enable");

  useEffect(() => {
    (async () => {
      let data = await getAllStatus();
      setAcStatus(data);
      let result = await retrieveAnticoagulantIds(data);
      setAnticoagulantStatementsIds(result);
    })().catch((err) => {
      showErrorMessage(err.message);
    });

    return () => {
      // When we leave the patient information section we need to reset save state to its initial value
      setInformation({ save: undefined });
    };
  }, []);

  useEffect(() => {
    // Request Patient Sync if supported by location
    if (isSyncSupported) dispatchSync(patient);
  }, [patient]);

  const getAnticoagulantFromServer = async () => {
    try {
      const anticoagulantStatement = await getAnticoagulantStatementV2(patient.id);

      if (!anticoagulantStatement) return;

      if (anticoagulantStatement?.has_error) {
        anticoagulantStatement["id"] = "";
      }

      if (data.anticoagulant) {
        setConclusionStatements(
          findConclusionStatementsFromIds(anticoagulantStatementsIds[data.anticoagulant.toUpperCase()])
        );
      }

      if (
        anticoagulantStatement.userSelectedStatements &&
        anticoagulantStatement.userSelectedStatements?.length > 0
      ) {
        setAnticoagulantStatementSelectedByUser(anticoagulantStatement.userSelectedStatements[0]);
      } else {
        setAnticoagulantStatementSelectedByUser("");
      }

      if (anticoagulantStatement.lastDateUpdated && anticoagulantStatement.lastUserUpdated) {
        const acLastUpdate = dayjs(anticoagulantStatement.lastDateUpdated).format(
          dateFormats.DATE_AND_TIME_DEFAULT
        );

        const userFulName = await fetchUserById(anticoagulantStatement.lastUserUpdated);
        setACUpdatedData({ date: acLastUpdate, name: userFulName });
      }
    } catch (error) {
      showErrorMessage(error.message);
    }
  };

  useEffect(() => {
    if (anticoagulantStatementsIds.length === 0) return;

    if (!featureAnticoagulantStatement) return;

    getAnticoagulantFromServer();
  }, [anticoagulantStatementsIds]);

  useEffect(() => {
    // Fetch Patient Data when Patient Sync gets an update
    hasSyncUpdate && fetchPatient();
  }, [hasSyncUpdate]);

  useEffect(() => {
    if (information.cancel) {
      if (!patient["anticoagulant"]) {
        patient["anticoagulant"] = "";
      }
      if (featureAnticoagulantStatement) {
        (async () => {
          await getAnticoagulantFromServer();
          if (patient.anticoagulant) {
            setConclusionStatements(
              findConclusionStatementsFromIds(anticoagulantStatementsIds[patient.anticoagulant.toUpperCase()])
            );
            patient["anticoagulantStatement"] = anticoagulantStatementSelectedByUser;
          } else {
            setConclusionStatements({});
          }
        })();
      } else {
        setAnticoagulantStatementSelectedByUser("");
      }
      setData(patient);
      setAcsFlag(false);
    }
  }, [information.cancel]);

  const showWarningMessage = (message) => {
    addToast(message, {
      appearance: "warning",
      autoDismiss: true,
    });
  };

  const showErrorMessage = (message) => {
    addToast(message, {
      appearance: "error",
      autoDismiss: true,
    });
  };

  const save = async () => {
    if (
      acsFlag &&
      featureAnticoagulantStatement &&
      data.anticoagulant &&
      anticoagulantStatementsIds[data.anticoagulant?.toUpperCase()] &&
      anticoagulantStatementsIds[data.anticoagulant?.toUpperCase()].length > 0 &&
      anticoagulantStatementSelectedByUser.length === 0
    ) {
      showWarningMessage("Anticoagulant statement has not been selected");
      return;
    }
    // Validation for Other Phone Field
    let otherPhone = data.otherPhone && data.otherPhone.trim();
    if (data.preferredPhoneType != PhoneType.Other.value) {
      // Clear Other Phone if is not the selected Preferred Phone Type
      otherPhone = "";
    } else if (!isValidUSPhoneNumber(otherPhone)) {
      // Show error message if no valid phone number has been set
      showWarningMessage(otherPhoneWarning);
      return false;
    }

    if (information.loading) return false;
    try {
      setInformation({ loading: true });
      const acStatusSelected = acStatus.find(
        (current) => data.anticoagulant && current.text === data.anticoagulant
      ) || {
        id: "",
        text: "",
      };
      let acPayload = {};
      if (acsFlag) {
        if (anticoagulantStatementSelectedByUser) {
          acPayload = {
            statement: anticoagulantStatementSelectedByUser,
            anticoagulantStatusId: acStatusSelected.id,
          };
        } else {
          acPayload = {
            anticoagulantStatusId: acStatusSelected.id,
          };
        }
      }

      // Final Payload after validation
      let payload = {
        ...data,
        ...acPayload,
        otherPhone,
      };

      const response = await fetchHelper.put("/patientmanagement/rest/patients/" + data.id, payload);
      await getAnticoagulantFromServer();
      setInformation({ loading: false, openForm: false });
      setPatient(response);
    } catch (err) {
      setInformation({ loading: false });
      console.log(err);
    } finally {
      setAcsFlag(false);
    }
  };

  useEffect(() => {
    setData(patient);
  }, [patient]);

  useEffect(() => {
    if (information.save) {
      save();
    }
  }, [information.save]);

  const onChange = (e) => {
    const element = e.target;
    const key = element.dataset.key;
    let value = element.value;
    if (key === "dob") {
      value = new Date(value).getTime();
    }
    if (featureAnticoagulantStatement && key === "anticoagulant") {
      const newStatements = findConclusionStatementsFromIds(anticoagulantStatementsIds[value.toUpperCase()]);
      setConclusionStatements(newStatements);
      setAnticoagulantStatementSelectedByUser("");
      setAcsFlag(true);
    }
    if (featureAnticoagulantStatement && key === "anticoagulantStatement") {
      setAnticoagulantStatementSelectedByUser(value);
      setAcsFlag(true);
    }
    setData((prev) =>
      produce(prev, (draft) => {
        draft[key] = value;
      })
    );
  };

  const readOnlyIdentifier = useMemo(() => state === "LOCKED" && !report, [state, report]);

  const serviceStatusOptions = [
    { key: "PENDING", value: "PENDING" },
    { key: "COMPLETE", value: "COMPLETE" },
    { key: "POC_ONLY", value: "POC-ONLY" },
  ];

  return (
    <div className="tabContent ptInformation">
      <div className="content">
        <div className="grpHdr patient-information-header">
          <h3 className="patient-information">Patient Information</h3>
          {isSyncSupported && <SyncStatusIcon status={syncStatus} date={syncResult?.updatedAt} />}
          {patient?.patientLocation[0]?.mrnCount > 1 && (
            <div className="container-with-tooltip">
              <Tooltip
                testId="duplicated-mrn-msg"
                text={`${patient?.patientLocation[0]?.mrnCount} Potential duplicate profile found(s). Please review for patient merge.`}
                isVisible={showTooltip}
              />
              <img
                src={duplicatedProfiles}
                id="duplicated-profile-icon"
                data-testid="duplicated-profile-icon"
                className="duplicated-profile-icon"
                onMouseOver={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                onClick={(e) => {
                  e.preventDefault();
                  history.push("/patients3", {
                    filters: {
                      "patientLocation.mrn": { ...mrnFilter, value: patient?.patientLocation[0]?.mrn },
                    },
                  });
                }}
              ></img>
            </div>
          )}
        </div>
        {patient.patientState === "LOCKED" && !user?.isServiceReport ? (
          <div style={{ fontSize: 14, marginBottom: 20 }}>
            * Patient information is in sync with EMR record. Please update EMR directly if changes to patient
            personal information are needed.
          </div>
        ) : null}
        <div className="row rowStretch ptInformation-row">
          <div className="col col-s-100 col-m-50 col33">
            <label>
              <span>Status</span>
              <select value={data.status} data-key="status" onChange={onChange}>
                <option value="">Select Status...</option>
                {["ACTIVE", "DECEASED", "REMOVED"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-s-100 col33 col-m-50 force-0m stats-col">
            <label>
              <span>State</span>
              <select
                value={data.patientState}
                data-key="patientState"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              >
                <option value="">Select State...</option>
                {["ACTIVE", "LOCKED"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-s-100 col33 col-m-50 showBs" />
        </div>
        <div className="row rowStretch ptInformation-row">
          <div className="col col-s-100 col-m-50 col33">
            <label>
              <span>First Name</span>
              <input
                value={data.name}
                data-testid="first-name"
                data-key="name"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className="col col-s-100 col-m-50 col33 force-0m">
            <label>
              <span>Middle Name</span>
              <input
                value={data.middleName}
                data-key="middleName"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className="col col-s-100 col-m-50 col33 noPadding-m-col">
            <label>
              <span>Last Name </span>
              <input
                value={data.family}
                data-key="family"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className="col col-s-100 col-m-50 col33 force-0m">
            <label className="inCal">
              <span>Date of Birth</span>
            </label>
            <DatePicker
              selected={(data.dob && new Date(data.dob)) || ""}
              disabled={readOnlyIdentifier}
              onChange={(date) => {
                setData((prev) =>
                  produce(prev, (draft) => {
                    draft["dob"] = date;
                  })
                );
              }}
            />
          </div>
          <div className="col col-s-100 col-m-50 col33">
            <label>
              <span>Gender</span>
              <select value={data.gender} data-key="gender" onChange={onChange} disabled={readOnlyIdentifier}>
                <option value="">Select Gender...</option>
                {["MALE", "FEMALE", "UNKNOWN", "NONBINARY", "OTHER"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-s-100 col-m-50 col33">
            <label>
              <span>Name Alert</span>
              <select value={data.nameAlert} data-key="nameAlert" onChange={onChange}>
                {["NO", "YES"].map((item) => (
                  <option key={item} value={Boolean(item !== "NO")}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="col col-s-100 col-m-50 col33"></div>
        </div>
        <div className="grpHdr">
          <h3>Contact Information</h3>
        </div>
        <div className="row rowStretch contact-information-row">
          <div className={`col col-m-50 ${featurePatientPreferredPhoneNumber ? "col25" : "col33"}`}>
            <label>
              <span>Email Address</span>
              <input
                type="email"
                value={data.emailAddress}
                data-key="emailAddress"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className={`col col-m-50 ${featurePatientPreferredPhoneNumber ? "col25" : "col33"}`}>
            <label>
              <span>Home Phone</span>{" "}
              <input
                type="text"
                value={data.homePhone}
                data-key="homePhone"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className={`col col-m-50 ${featurePatientPreferredPhoneNumber ? "col25" : "col33"}`}>
            <label>
              <span>Mobile Phone</span>{" "}
              <input
                type="text"
                value={data.mobilePhone}
                data-key="mobilePhone"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          {featurePatientPreferredPhoneNumber && (
            <div className="col col-s-100 col25">
              <label htmlFor="preferred-number">
                <span>Preferred Contact Number</span>
                <PreferredPhoneField data={data} setter={setData} />
              </label>
            </div>
          )}
        </div>
        <div className="row rowStretch contact-information-row">
          <div className="col col-s-100 col50 address-line-col">
            <label>
              <span>Address Line 1</span>{" "}
              <input
                type="text"
                value={data.address1}
                data-key="address1"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className="col col-s-100 col50 address-line-col">
            <label>
              <span>Address Line 2</span>{" "}
              <input
                type="text"
                value={data.address2}
                data-key="address2"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
        </div>
        <div className="row rowStretch address-row">
          <div className="col col-s-100 col25">
            <label>
              <span>City</span>
              <input
                type="text"
                value={data.city}
                data-key="city"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className="col col-s-100 col25 form-dropdown">
            <label>
              <span>State</span>
              <SearchMultiSelect
                disabled={readOnlyIdentifier}
                options={Object.keys(STATES).map((state) => ({ label: STATES[state], value: state }))}
                maxSelect={1}
                hasPreview={false}
                initialSelected={[{ value: getStateAbbreviation(data.state) }]}
                onChange={(value) => {
                  setData((prev) =>
                    produce(prev, (draft) => {
                      draft.state = value[0];
                    })
                  );
                }}
              />
            </label>
          </div>
          <div className="col col-s-100 col25">
            <label>
              <span>Zip</span>{" "}
              <input
                type="text"
                value={data.zip}
                data-key="zip"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
          <div className="col col-s-100 col25 ptInformation-row">
            <label>
              <span>Country</span>
              <input
                type="text"
                value={data.country}
                data-key="country"
                onChange={onChange}
                disabled={readOnlyIdentifier}
              />
            </label>
          </div>
        </div>
        <div className="grpHdr">
          <h3>Clinical Info</h3>
        </div>
        <div className={"row rowStretch ptInformation-row"}>
          <div
            className={cn("col col-s-100 no-padding-left", {
              col33: !featureAnticoagulantStatement,
              col10: featureAnticoagulantStatement,
            })}
          >
            <label>
              <span>Service Status</span>
              <select value={data.serviceStatus} data-key="serviceStatus" onChange={onChange}>
                <option value="NULL">-</option>
                {serviceStatusOptions.map(({ key, value }) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div
            className={cn("col col-s-100", {
              col33: !featureAnticoagulantStatement,
              col20: featureAnticoagulantStatement,
            })}
          >
            <label>
              <span>Pacemaker Dependency</span>
              <select value={data.pacemakerDep} data-key="pacemakerDep" onChange={onChange}>
                <option value="">-</option>
                {["YES", "NO"].map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div
            className={cn("col col-s-100", {
              col33: !featureAnticoagulantStatement,
              col20: featureAnticoagulantStatement,
            })}
          >
            <label>
              <span>Anticoagulant Status</span>
              <select
                value={data.anticoagulant}
                data-key="anticoagulant"
                onChange={onChange}
                id={"anticoagulant-status-dropdown"}
              >
                <option style={{ display: "none" }} value="">
                  -
                </option>
                {acStatus.map((item) => (
                  <option key={item.id} data-key={item.id} value={item.text}>
                    {item.text}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {featureCheck("anticoagulantStatement.enable") && (
            <>
              <div className="col col-s-100 col30">
                <label>
                  <span>Anticoagulant Statement</span>
                  <select
                    value={anticoagulantStatementSelectedByUser}
                    data-key="anticoagulantStatement"
                    id={"anticoagulant-statement-dropdown"}
                    onChange={onChange}
                    disabled={Object.keys(conclusionStatements).length === 0}
                  >
                    {Object.keys(conclusionStatements).length === 0 && <option value="-">-</option>}
                    <option value="none" style={{ display: "none" }}>
                      -
                    </option>
                    {Object.keys(conclusionStatements).map((key) => {
                      return (
                        <option key={key} value={key}>
                          {conclusionStatements[key]}
                        </option>
                      );
                    })}
                  </select>
                </label>
              </div>
              {ACUpdatedData.name && (
                <div className="col col-s-100 col20">
                  <p className="ac-date-updated">
                    Anticoagulant last updated by {ACUpdatedData.name} {ACUpdatedData.date} UTC
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!information.openForm ? <div className="closed-form-cover"></div> : null}
    </div>
  );
};

export default Information;
